"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const trustItems = ["3-day free trial", "No credit card to start", "Cancel anytime", "EN/ES bilingual"];
const pricingPlans = [{
  name: "Starter",
  price: "$20",
  description: "Perfect for a clean, professional site that just works.",
  features: ["1 published website", "15 AI credits per month (1 credit = 1 website change)", "2 full site generations per month", "Free Cynergia subdomain", "Bilingual EN/ES", "Mobile responsive", "SSL certificate included", "90+ PageSpeed performance", "Basic contact forms", "Email support"],
  ctaText: "Start free trial",
  ctaLink: "https://app.cynergia.co/sign-up?plan=starter",
  isFeatured: false
}, {
  name: "Standard",
  price: "$30",
  description: "Everything most small businesses need to grow online.",
  features: ["Everything in Starter", "50 AI credits per month", "4 full site generations per month", "Custom domain included", "Contact forms with email capture", "Online reservations and bookings", "Google Analytics integration", "Meta Pixel integration", "URL-to-site cloning", "Priority email support"],
  ctaText: "Start free trial",
  ctaLink: "https://app.cynergia.co/sign-up?plan=pro",
  isFeatured: true,
  badge: "Most Popular"
}, {
  name: "Pro",
  price: "$99",
  description: "Built for agencies, freelancers, and resellers managing client sites.",
  features: ["5 published websites", "75 AI credits per month", "10 full site generations per month", "Custom domains on every site", "Bilingual EN/ES on every site", "Done-for-you build (one-time) — our team builds your first client site", "Priority phone and email support", "Dedicated account contact"],
  ctaText: "Start free trial",
  ctaLink: "https://app.cynergia.co/sign-up?plan=business",
  isFeatured: false
}];
const comparisonRows = [{
  feature: "Bilingual EN/ES out of the box",
  cynergia: true,
  wix: false,
  squarespace: false
}, {
  feature: "AI edits your live site by chat",
  cynergia: "Unlimited at Standard",
  wix: "Limited credits",
  squarespace: "Not available"
}, {
  feature: "Live support from a real human",
  cynergia: true,
  wix: "Chatbot first",
  squarespace: "Chatbot first"
}, {
  feature: "Manage multiple sites from one account",
  cynergia: "Yes — up to 5 on Pro",
  wix: "One site per account",
  squarespace: "One site per account"
}, {
  feature: "WordPress backend (you own your site)",
  cynergia: true,
  wix: false,
  squarespace: false
}, {
  feature: "Done-for-you build option",
  cynergia: true,
  wix: false,
  squarespace: false
}, {
  feature: "Custom domain",
  cynergia: "Included",
  wix: true,
  squarespace: true
}, {
  feature: "Online Bookings",
  cynergia: "Included",
  wix: "Paid Add-on",
  squarespace: "Paid Add-on"
}, {
  feature: "Real cost with bilingual + bookings + domain",
  cynergia: "$30/mo (included)",
  wix: "~$50/mo",
  squarespace: "~$45/mo"
}];
const faqs = [{
  q: "Can I really try it free?",
  a: "Yes. You get 3 full days to build, customize, and preview your site. You only pay when you publish — and you can cancel any time before then with zero charge."
}, {
  q: "What happens after the free trial?",
  a: "If you decide to publish, you pick a plan ($20, $30, or $99/mo) and your site goes live. If you don't, your draft is saved for 30 days in case you change your mind — no card charged."
}, {
  q: "Can I switch plans later?",
  a: "Anytime. Upgrade or downgrade from your dashboard — the change kicks in immediately and we prorate the difference."
}, {
  q: "Do I own my website?",
  a: "Yes. Your site is built on WordPress, which means you can export it, move it, or take it elsewhere. We're not a walled garden."
}, {
  q: "Is hosting really included?",
  a: "Yes — every plan includes fast, secure hosting, free SSL, and automatic backups. You don't need a separate hosting provider."
}, {
  q: "What if I need help building my site?",
  a: "Starter and Standard plans get email support. Pro plan includes a one-time done-for-you build — our team builds your first client site based on a short questionnaire."
}, {
  q: "Do you support Spanish?",
  a: "Yes. Every site can be built fully bilingual EN/ES from day one. Your dashboard is also available in Spanish."
}, {
  q: "Can I cancel anytime?",
  a: "Yes. Cancel from your dashboard in two clicks. No phone calls, no retention specialists, no fees."
}];
export default function PricingHero() {
  const {
    ref: heroRef,
    isVisible: isHeroVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardsRef,
    isVisible: areCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: tableRef,
    isVisible: isTableVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: faqRef,
    isVisible: isFaqVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  return <section id="pricinghero" ref={heroRef} className={`dark bg-background text-foreground relative overflow-hidden transition-all duration-700 ${isHeroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/20 blur-[120px] rounded-lg pointer-events-none opacity-50" />

      <div className="page-container relative z-10 py-20">
        <div className="flex flex-col items-center text-center">
          <span className="text-sm font-medium text-foreground mb-4 tracking-wide uppercase">
            Pricing
          </span>
          <h1 className="font-default text-4xl md:text-5xl lg:text-[66px] font-extrabold tracking-tight leading-[1.15] text-foreground max-w-4xl">
            Real websites. Real pricing. No surprises.
          </h1>
          <p className="text-lg text-muted-foreground max-w-[640px] mt-4">
            Try it free for 3 days. Pick a plan when you're ready to go live.
            Cancel anytime — your site stays yours.
          </p>

          <div className="flex flex-wrap justify-center gap-6 mt-8">
            {trustItems.map((item, i) => <div data-index={i} key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                <Check className="size-4 text-secondary" />
                <span>{item}</span>
              </div>)}
          </div>
        </div>

        <div ref={cardsRef} className={`grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto mt-20 transition-all duration-700 delay-100 ${areCardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {pricingPlans.map((plan, i) => <div data-index={i} key={plan.name} className={`relative flex flex-col p-8 rounded-2xl bg-card border transition-all duration-300 hover:shadow-xl hover:shadow-primary/20 ${plan.isFeatured ? "border-2 border-primary shadow-lg shadow-primary/30 md:-translate-y-4" : "border-border"}`}>
              {plan.badge && <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground uppercase tracking-wider text-[10px] font-bold px-3 py-1 rounded-lg">
                    {plan.badge}
                  </Badge>
                </div>}

              <h3 className="font-default text-lg font-semibold text-foreground">
                {plan.name}
              </h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-default text-4xl md:text-5xl font-extrabold text-foreground">
                  {plan.price}
                </span>
                <span className="text-base text-muted-foreground font-normal">
                  /month
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-2 min-h-[40px]">
                {plan.description}
              </p>

              <Separator className="my-6 bg-border/50" />

              <ul className="flex-1 space-y-3 mb-8">
                {plan.features.map((feature, j) => <li data-index={j} key={j} className="flex items-start gap-3 text-sm text-foreground leading-relaxed">
                    <Check className="size-4 text-secondary shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>)}
              </ul>

              <Button asChild variant={plan.isFeatured ? "default" : "secondary"} className={`w-full py-6 text-sm font-semibold rounded-lg transition-transform ${plan.isFeatured ? "hover:scale-[1.02]" : ""}`}>
                <Link to={plan.ctaLink}>{plan.ctaText}</Link>
              </Button>
            </div>)}
        </div>

        <div ref={tableRef} className={`mt-32 transition-all duration-700 delay-200 ${isTableVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-10">
            <h2 className="font-default text-3xl md:text-4xl font-bold text-foreground">
              How Cynergia compares.
            </h2>
            <p className="text-base text-muted-foreground max-w-[560px] mx-auto mt-3">
              What you get with us vs. the other guys — at a glance.
            </p>
          </div>

          <div className="max-w-5xl mx-auto overflow-x-auto pb-4">
            <div className="min-w-[800px] rounded-2xl border border-border bg-card/30 overflow-hidden">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="p-4 md:p-5 font-default font-semibold text-foreground w-1/3">
                      Feature
                    </th>
                    <th className="p-4 md:p-5 font-default font-semibold text-foreground bg-primary/10 text-center w-1/6">
                      Cynergia
                    </th>
                    <th className="p-4 md:p-5 font-default font-semibold text-muted-foreground text-center w-1/6">
                      Wix
                    </th>
                    <th className="p-4 md:p-5 font-default font-semibold text-muted-foreground text-center w-1/6">
                      Squarespace
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, i) => <tr data-index={i} key={i} className="border-b border-border/50 hover:bg-card/50 transition-colors">
                      <td className="p-4 md:p-5 text-sm text-foreground font-medium">
                        {row.feature}
                      </td>
                      <td className="p-4 md:p-5 text-center bg-primary/10">
                        {typeof row.cynergia === "boolean" ? row.cynergia ? <Check className="size-5 text-secondary mx-auto" /> : <X className="size-5 text-destructive mx-auto" /> : <span className="text-sm font-bold text-foreground">
                            {row.cynergia}
                          </span>}
                      </td>
                      <td className="p-4 md:p-5 text-center">
                        {typeof row.wix === "boolean" ? row.wix ? <Check className="size-5 text-secondary mx-auto" /> : <X className="size-5 text-muted-foreground/50 mx-auto" /> : <div className="flex items-center justify-center gap-2">
                            <X className="size-5 text-muted-foreground/50" />
                            <span className="text-sm text-muted-foreground">
                              {row.wix}
                            </span>
                          </div>}
                      </td>
                      <td className="p-4 md:p-5 text-center">
                        {typeof row.squarespace === "boolean" ? row.squarespace ? <Check className="size-5 text-secondary mx-auto" /> : <X className="size-5 text-muted-foreground/50 mx-auto" /> : <div className="flex items-center justify-center gap-2">
                            <X className="size-5 text-muted-foreground/50" />
                            <span className="text-sm text-muted-foreground">
                              {row.squarespace}
                            </span>
                          </div>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
              <p className="text-xs text-muted-foreground mt-3 px-4 pb-4">Cynergia Standard ($30) includes bilingual EN/ES, online bookings, and a custom domain. To match this on Wix, you need Wix Business ($36) + a multilingual plugin (~$15/mo). On Squarespace, you need Squarespace Business ($33) + paid add-ons. Pricing reflects publicly listed monthly rates as of 2026.</p>
            </div>
          </div>
        </div>

        <div ref={faqRef} className={`max-w-3xl mx-auto mt-32 transition-all duration-700 delay-300 ${isFaqVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-3xl md:text-4xl font-bold text-foreground text-center mb-10">
            Pricing questions, answered.
          </h2>
          <div className="space-y-1">
            {faqs.map((faq, i) => <div data-index={i} key={i} className="border-b border-border/50 last:border-0">
                <Button variant="ghost" onClick={() => toggleFaq(i)} className="gap-2 whitespace-nowrap rounded-md disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive hover:bg-primary dark:hover:bg-primary/50 has-[>svg]:px-3 w-full flex items-center justify-between py-5 text-left h-auto font-default text-base font-semibold text-foreground hover:text-primary transition-colors px-0" aria-expanded={openFaqIndex === i}>
                  <span>{faq.q}</span>
                  <ChevronDown className={`size-5 text-muted-foreground transition-transform duration-300 shrink-0 ${openFaqIndex === i ? "rotate-180" : ""}`} />
                </Button>
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openFaqIndex === i ? "max-h-40 opacity-100 pb-5" : "max-h-0 opacity-0"}`}>
                  <p className="text-[15px] text-muted-foreground leading-relaxed pr-8">
                    {faq.a}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}